import React, { useState, useContext, useReducer, useEffect, useRef } from 'react';
import request from 'services/http';
import { tap, finalize } from 'rxjs/operators';
import { paramsToSearchURL } from 'utils';
import { Icon, Table, Form, Tab } from 'semantic-ui-react';
import { numFormat, dateIdToFormat } from '@rededompedro/utils';
import { GlobalContext } from 'globalContext';
import InputMask from 'react-input-mask';
import produce from 'immer';
import history from 'services/history';
import { toast } from 'react-toastify';
import './Cupons.scss';

const initialFormValues = {
    cuponsVenda: {
        dataVenda: '',
        unidade: null,
        formaPgto: null,
    },
    cuponsFaturados: {
        dataFatura: '',
        unidade: null,
        formaPgto: null,
    },
    analiseConf: {
        periodoStart: '',
        periodoEnd: '',
        tipoConf: null,
    },
};

function formReducer(state, action) {
    return produce(state, (draft) => {
        draft[action.tab][action.field] = action.value
    });
}

function toSQLDate(date) {
    return date.split('/').reverse().join('-');
}

const Positive = () => <Icon name='thumbs up outline' color='green' />;
const Negative = () => <Icon name='thumbs down outline' color='red' />;
function statusDocsystem(exists, faturado, op) {
    if (!exists) {
        return <Negative />;
    } else if (op === 'cupons-faturados' && !faturado) {
        return <Negative />;
    } else {
        return <Positive />;
    }
}

function Cupons(props) {

    const [data, setData] = useState([]);
    const [analiseConfData, setAnaliseConfData] = useState([]);
    const [globalState] = useContext(GlobalContext);
    const [formState, setFormState] = useReducer(formReducer, initialFormValues);
    const previousSearchParams = useRef();
    const [loading, setLoading] = useState(false);
    const [loadingConf, setLoadingConf] = useState(false);
    const [activeTab, setActiveTab] = useState(null);
    const [confStatus, setConfStatus] = useState(null);

    function cuponsVendaSubmit() {
        const params = { ...formState.cuponsVenda, dataVenda: toSQLDate(formState.cuponsVenda.dataVenda), op: 'cupons-venda' };
        history.push({ search: paramsToSearchURL(params) });
    }

    function cuponsFaturadosSubmit() {
        const params = { ...formState.cuponsFaturados, dataFatura: toSQLDate(formState.cuponsFaturados.dataFatura), op: 'cupons-faturados' };
        history.push({ search: paramsToSearchURL(params) });
    }

    function analiseConfSubmit() {
        const { periodoStart, periodoEnd, tipoConf } = formState.analiseConf;
        const params = { periodo: [toSQLDate(periodoStart), toSQLDate(periodoEnd)], tipoConf, op: 'analise-conf' };
        history.push({ search: paramsToSearchURL(params) });
    }

    function onFieldChange(ev, data) {
        const value = data ? data.value : ev.target.value;
        const name = data ? data.name : ev.target.name;
        const tab = data ? data['data-tab'] : ev.target.dataset.tab;
        setFormState({ tab, field: name, value });
    }

    useEffect(() => {
        const search = props.location.search;
        if (search !== '' && search !== previousSearchParams.current) {
            setConfStatus(null);
            setLoading(true);
            setData([]);
            setAnaliseConfData([]);
            const op = new URLSearchParams(search).get('op');
            const url = op === 'analise-conf'
                ? `/recebiveis/cupons/analise-conferencia${search}`
                : `/recebiveis/cupons${search}`;

            const req = request({ method: 'GET', url }).pipe(
                tap(({ response }) => {
                    if (op === 'analise-conf') {
                        setAnaliseConfData(response);
                    } else {
                        setData(response);
                    }
                }),
                finalize(() => {
                    previousSearchParams.current = props.location.search;
                    setLoading(false);
                    req.unsubscribe();
                }),
            ).subscribe();
        }
    }, [props.location.search]);

    function handleConference() {
        setLoadingConf(true);
        if (data.filter(x => !x.docsystem).length > 0) {
            toast.warn('Existem pendencias na conferência, por favor, resolva as mesmas para marcar como conferido.');
            setLoadingConf(false);
        } else {
            const ids = data.map(x => x.id);
            const search = props.location.search;
            const req = request({ method: 'POST', url: `/recebiveis/cupons/conf`, body: { ids, search } }).pipe(
                tap(({ response }) => {
                    setLoadingConf(false);
                    setConfStatus('ok');
                    toast.success('Conferência realizada com sucesso.');
                }),
                finalize(() => {
                    req.unsubscribe();
                }),
            ).subscribe();
        }
    }

    useEffect(() => {
        if (data.length) {
            const ids = data.map(x => x.id);
            const req = request({ method: 'GET', url: `/recebiveis/cupons/check-conf`, params: { ids } }).pipe(
                tap(({ response }) => setConfStatus(response)),
                finalize(() => {
                    req.unsubscribe();
                }),
            ).subscribe();
        }
    }, [data]);

    return (
        <div id="recebiveis-cupons">
            <div id="filters">
                <Tab
                    renderActiveOnly={false}
                    onTabChange={(_, data) => setActiveTab(data.activeIndex)}
                    panes={[
                        {
                            menuItem: { key: 'cupons-venda', content: 'Cupons de Venda', icon: 'file alternate outline' }, pane: (
                                <Tab.Pane key='cupons_venda'>
                                    <Form onSubmit={cuponsVendaSubmit}>
                                        <Form.Group>
                                            <Form.Field required>
                                                <label>Data da Venda</label>
                                                <InputMask
                                                    name="dataVenda"
                                                    data-tab="cuponsVenda"
                                                    mask="99/99/9999"
                                                    value={formState.cuponsVenda.dataVenda}
                                                    onChange={onFieldChange}
                                                />
                                            </Form.Field>
                                            <Form.Select
                                                name="unidade"
                                                data-tab="cuponsVenda"
                                                style={{ minWidth: 220 }}
                                                selectOnBlur={false}
                                                search
                                                label='Unidade'
                                                options={(globalState.empresas || [])
                                                    .filter(x => x.tipo === 'POSTO')
                                                    .map(x => ({ key: x.codAS, value: x.codAS, text: x.nomeEmpresa }))}
                                                onChange={onFieldChange}
                                                required
                                            />
                                            <Form.Select
                                                name="formaPgto"
                                                data-tab="cuponsVenda"
                                                clearable
                                                label='Forma Pgto.'
                                                selectOnBlur={false}
                                                options={[
                                                    { key: 12, value: 12, text: 'CTF' },
                                                    { key: 9, value: 9, text: 'CARTA FRETE' },
                                                    { key: 3, value: 3, text: 'NOTAS A PRAZO' },
                                                    { key: 440, value: 440, text: 'PRÓ-FROTAS' },
                                                    { key: 141, value: 141, text: 'VALE' },
                                                ]}
                                                onChange={onFieldChange}
                                            />
                                            <Form.Button
                                                primary label='&nbsp;'
                                                icon='cogs'
                                                content='Processar'
                                                loading={loading}
                                                disabled={loading}
                                            />
                                            {confStatus && (
                                                <>
                                                {confStatus !== 'ok' && (
                                                    <Form.Button
                                                        type='button'
                                                        label='&nbsp;'
                                                        color='green'
                                                        icon='check'
                                                        content='Conferir'
                                                        onClick={handleConference}
                                                        loading={loadingConf}
                                                    />
                                                )}
                                                <span style={{ marginTop: 38, fontWeight: 'bold' }}>{confStatus === 'ok' ? 'Tudo conferido.' : confStatus === 'parcial' ? 'Parcialmente conferido.' : 'Não conferido.'}</span>
                                                </>
                                            )}
                                        </Form.Group>
                                    </Form>
                                    {!loading && (
                                        <div className='status'>
                                            <span><b>Total</b>: {data.length}</span>
                                            {/* TODO: melhorar */}
                                            <span><b>Pendente DS</b>: {data.filter(x => !x.docsystem).length > 0 ? <b style={{ fontWeight: 'bold', color: 'red' }}>{data.filter(x => !x.docsystem).length}</b> : <b style={{ fontWeight: 'bold', color: 'green' }}>Nenhum.</b>}</span>
                                            <span><b>Pendente RDP</b>: {data.filter(x => !x.s3).length > 0 ? <b style={{ fontWeight: 'bold', color: 'red' }}>{data.filter(x => !x.s3).length}</b> : <b style={{ fontWeight: 'bold', color: 'green' }}>Nenhum.</b>}</span>
                                        </div>
                                    )}
                                </Tab.Pane>
                            )
                        },
                        {
                            menuItem: { key: 'cupons-faturados', content: 'Cupons Faturados', icon: 'file alternate outline' }, pane: (
                                <Tab.Pane key='cupons_faturados'>
                                    <Form onSubmit={cuponsFaturadosSubmit}>
                                        <Form.Group>
                                            <Form.Field required>
                                                <label>Data da Fatura</label>
                                                <InputMask
                                                    name="dataFatura"
                                                    data-tab="cuponsFaturados"
                                                    mask="99/99/9999"
                                                    value={formState.cuponsFaturados.data_fatura}
                                                    onChange={onFieldChange}
                                                />
                                            </Form.Field>
                                            <Form.Select
                                                name="unidade"
                                                data-tab="cuponsFaturados"
                                                style={{ minWidth: 220 }}
                                                selectOnBlur={false}
                                                search
                                                label='Unidade'
                                                options={(globalState.empresas || []).filter(x => x.tipo === 'POSTO').map(x => ({ key: x.codAS, value: x.codAS, text: x.nomeEmpresa }))}
                                                onChange={onFieldChange}
                                                required
                                            />
                                            <Form.Select
                                                name="formaPgto"
                                                data-tab="cuponsFaturados"
                                                clearable
                                                label='Forma Pgto.'
                                                selectOnBlur={false}
                                                onChange={onFieldChange}
                                                options={[
                                                    { key: 148, value: 148, text: 'CTF' },
                                                    { key: 115, value: 115, text: 'CARTA FRETE' },
                                                    { key: 80, value: 80, text: 'NOTAS A PRAZO' },
                                                    { key: 441, value: 441, text: 'PRÓ-FROTAS' },
                                                    { key: 116, value: 116, text: 'VALE' },
                                                ]}
                                            />
                                            <Form.Button
                                                primary
                                                label='&nbsp;'
                                                icon='cogs'
                                                content='Processar'
                                                loading={loading}
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Form>
                                </Tab.Pane>
                            )
                        },
                        {
                            menuItem: { key: 'analise-conferencia', content: 'Análise de Conferências', icon: 'tasks' }, pane: (
                                <Tab.Pane key='analise_conferencia'>
                                    <Form onSubmit={analiseConfSubmit}>
                                        <Form.Group>
                                            <Form.Field required>
                                                <label>Período da Conferência</label>
                                                <div className='join-inputs'>
                                                    <InputMask
                                                        name="periodoStart"
                                                        data-tab="analiseConf"
                                                        mask="99/99/9999"
                                                        value={formState.analiseConf.periodoStart}
                                                        onChange={onFieldChange}
                                                    />
                                                    <InputMask
                                                        name="periodoEnd"
                                                        data-tab="analiseConf"
                                                        mask="99/99/9999"
                                                        value={formState.analiseConf.periodoEnd}
                                                        onChange={onFieldChange}
                                                    />
                                                </div>
                                            </Form.Field>
                                            <Form.Select
                                                name="tipoConf"
                                                data-tab="analiseConf"
                                                clearable
                                                label='Tipo de Conferência'
                                                selectOnBlur={false}
                                                onChange={onFieldChange}
                                                options={[
                                                    { key: 'venda', value: 'venda', text: 'Cupons de Venda' },
                                                    { key: 'faturado', value: 'faturado', text: 'Cupons Faturados' },
                                                ]}
                                                required
                                            />
                                            <Form.Button
                                                primary
                                                label='&nbsp;'
                                                icon='cogs'
                                                content='Processar'
                                                loading={loading}
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Form>
                                </Tab.Pane>
                            )
                        }
                    ]} />
            </div>
            {activeTab !== 2 ? (
                <Table selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Nº Cupom</Table.HeaderCell>
                            <Table.HeaderCell>Data Cupom</Table.HeaderCell>
                            <Table.HeaderCell>Cliente</Table.HeaderCell>
                            <Table.HeaderCell>Forma pgto.</Table.HeaderCell>
                            <Table.HeaderCell>Valor</Table.HeaderCell>
                            <Table.HeaderCell>Pasta Docsystem</Table.HeaderCell>
                            <Table.HeaderCell style={{ textAlign: 'center' }}>Status Docsystem</Table.HeaderCell>
                            <Table.HeaderCell style={{ textAlign: 'center' }}>Cupom/RDPOnline</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {loading && (
                            <Table.Row>
                                <Table.Cell style={{ padding: 50 }} textAlign='center' colSpan={8}>Gerando relatório... Isso pode levar minutos, por favor aguarde.</Table.Cell>
                            </Table.Row>
                        )}
                        {!loading && !data.length && window.location.search !== '' && (
                            <Table.Row>
                                <Table.Cell style={{ padding: 50 }} textAlign='center' colSpan={8}>Não foram retornados dados para esta consulta.</Table.Cell>
                            </Table.Row>
                        )}
                        {!loading && window.location.search === '' && (
                            <Table.Row>
                                <Table.Cell style={{ padding: 50 }} textAlign='center' colSpan={8}>Preencha o formulário e clique em Processar.</Table.Cell>
                            </Table.Row>
                        )}
                        {data.map((x, i) => (
                            <Table.Row key={i}>
                                <Table.Cell>{x.documento}</Table.Cell>
                                <Table.Cell>{dateIdToFormat(x.emissao, 'dd/MM/yyyy - HH:mm')}</Table.Cell>
                                <Table.Cell>{x.cod_cliente}</Table.Cell>
                                <Table.Cell>{x.forma_pgto}</Table.Cell>
                                <Table.Cell>R${numFormat(x.valor)}</Table.Cell>
                                <Table.Cell>{x.docsystem ? (x.faturado ? 'Faturados' : 'Para Faturar') : '--'}</Table.Cell>
                                <Table.Cell style={{ textAlign: 'center' }}>{statusDocsystem(x.docsystem, x.faturado, new URL(window.location).searchParams.get('op'))}</Table.Cell>
                                <Table.Cell style={{ textAlign: 'center' }}>{x.s3 ? <Icon name='thumbs up outline' color='green' /> : <Icon name='thumbs down outline' color='red' />}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            ) : (
                <Table selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Cod.</Table.HeaderCell>
                            <Table.HeaderCell>Unidade</Table.HeaderCell>
                            <Table.HeaderCell textAlign='center'>Qtd. Cupons AS</Table.HeaderCell>
                            <Table.HeaderCell textAlign='center'>Qtd. Cupons Conferidos</Table.HeaderCell>
                            <Table.HeaderCell textAlign='center'>Status</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {loading && (
                            <Table.Row>
                                <Table.Cell style={{ padding: 50 }} textAlign='center' colSpan={8}>Gerando relatório... Isso pode levar minutos, por favor aguarde.</Table.Cell>
                            </Table.Row>
                        )}
                        {!loading && !analiseConfData.length && window.location.search !== '' && (
                            <Table.Row>
                                <Table.Cell style={{ padding: 50 }} textAlign='center' colSpan={8}>Não foram retornados dados para esta consulta.</Table.Cell>
                            </Table.Row>
                        )}
                        {!loading && window.location.search === '' && (
                            <Table.Row>
                                <Table.Cell style={{ padding: 50 }} textAlign='center' colSpan={8}>Preencha o formulário e clique em Processar.</Table.Cell>
                            </Table.Row>
                        )}
                        {analiseConfData.map((x, i) => (
                            <Table.Row key={i}>
                                <Table.Cell>{x.cod_unidade}</Table.Cell>
                                <Table.Cell>{x.unidade}</Table.Cell>
                                <Table.Cell textAlign='center'>{x.qtd_cupons}</Table.Cell>
                                <Table.Cell textAlign='center'>{x.qtd_cupons_conf}</Table.Cell>
                                <Table.Cell textAlign='center'>{x.qtd_cupons <= x.qtd_cupons_conf ? <Icon name='check circle' color='green' /> : <Icon name='remove circle' color='red' />}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
}
export default React.memo(Cupons);
